import { BlockType } from '../../models/enums';
import { BlockFactory } from '../../block-manager/services/block-factory';
import * as constants from '../../common/constant';
import { toDomCol, toModelRow } from './table-utils';
/**
 * Generates plain text representation of blocks for external clipboard
 *
 * @param {BlockModel[]} blocks - Block models to convert to plain text
 * @returns {string} Plain text representation of blocks
 */
export function generatePlainTextForExternalClipboard(blocks) {
    const textParts = [];
    blocks.forEach((block) => {
        if (block.blockType === BlockType.BulletList) {
            textParts.push(`• ${getBlockText(block)}\n`);
        }
        else if (block.blockType === BlockType.NumberedList) {
            const blockElement = document.getElementById(block.id);
            const listItem = blockElement.querySelector('li');
            const computedStyle = window.getComputedStyle(listItem);
            const marker = computedStyle.getPropertyValue('list-style-type');
            textParts.push(`${marker}${getBlockText(block)}\n`);
        }
        else if (block.blockType === BlockType.Divider) {
            textParts.push('---\n');
        }
        else {
            textParts.push(`${getBlockText(block)}\n`);
        }
    });
    return textParts.join('');
}
/**
 * Creates block model from plain clipboard text
 *
 * @param {string} text - Text from clipboard
 * @returns {BlockModel[]} Array of block models
 */
export function createBlocksFromPlainText(text) {
    const lines = text.split(/\r?\n/);
    const blocks = [];
    lines.forEach((line) => {
        if (line.trim() === '') {
            return;
        }
        const bulletMatch = line.match(/^[\s]*[•\-*]\s+(.*)/);
        if (bulletMatch) {
            blocks.push(BlockFactory.createBulletListBlock({
                content: [BlockFactory.createTextContent({ content: bulletMatch[1] })]
            }));
            return;
        }
        const numberedMatch = line.match(/^[\s]*(\d+)[.)]\s+(.*)/);
        if (numberedMatch) {
            blocks.push(BlockFactory.createNumberedListBlock({
                content: [BlockFactory.createTextContent({ content: numberedMatch[2] })]
            }));
            return;
        }
        blocks.push(BlockFactory.createParagraphBlock({
            content: [BlockFactory.createTextContent({ content: line })]
        }));
    });
    return blocks;
}
/**
 * Gets text content from a block model
 *
 * @param {BlockModel} block - Block model to extract text from
 * @returns {string} Plain text content from block
 */
export function getBlockText(block) {
    if (!block.content || block.content.length === 0) {
        return '';
    }
    return block.content.map((content) => content.content).join('');
}
/**
 * Checks if HTML content contains block-level elements
 *
 * @param {HTMLElement} container - Container with HTML content
 * @returns {boolean} True if contains block-level elements
 */
export function isBlockLevelContent(container) {
    const blockTags = ['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'UL', 'OL', 'LI', 'BLOCKQUOTE', 'HR', 'TABLE', 'IMG'];
    return Array.from(container.querySelectorAll('*')).some((el) => blockTags.indexOf(el.tagName) !== -1);
}
/**
 * Unwraps container element if needed
 *
 * @param {HTMLElement} container - Container to potentially unwrap
 * @returns {HTMLElement} Unwrapped container or original
 */
export function unWrapContainer(container) {
    const firstChild = container.firstElementChild;
    if (container.childElementCount === 1
        && isBlockLevelContent(firstChild)
        && (firstChild.tagName === 'SPAN' || firstChild.tagName === 'DIV')) {
        const newContainer = document.createElement('div');
        Array.from(firstChild.childNodes).forEach((child) => newContainer.appendChild(child.cloneNode(true)));
        return newContainer;
    }
    return container;
}
export function writeTableClipboardPayload(dt, payload, html, text) {
    dt.setData('text/blockeditor-table', JSON.stringify(payload));
    dt.setData('text/html', html);
    dt.setData('text/plain', text);
}
export function readTableClipboardPayload(dt) {
    try {
        const data = dt.getData('text/blockeditor-table');
        if (!data) {
            return null;
        }
        const parsed = JSON.parse(data);
        if (parsed && parsed.type === 'table') {
            return parsed;
        }
        return null;
    }
    catch (_a) {
        return null;
    }
}
export function buildTableClipboardPayload(tableBlockEl, blockModel) {
    if (!tableBlockEl || !blockModel) {
        return null;
    }
    const tableEl = tableBlockEl.querySelector('table');
    const props = blockModel.properties;
    let focusedCells = [];
    const selectedRows = tableEl.querySelectorAll('tr.e-row-selected');
    const selectedCols = tableBlockEl.querySelectorAll('td.e-col-selected, th.e-col-selected');
    let targetEle;
    let selector;
    if (selectedRows.length > 0) {
        // Multi-row: collect cells from ALL selected rows
        const allFocusedCells = [];
        selectedRows.forEach((row) => {
            allFocusedCells.push(...Array.from(row.querySelectorAll('td:not(.e-row-number)')));
        });
        targetEle = tableEl; // Use whole table as base
        selector = 'td:not(.e-row-number)';
        focusedCells = allFocusedCells;
    }
    else if (selectedCols.length > 0) {
        // skips header row for column copy
        focusedCells = [];
        // Loop only over body rows (tBodies[0])
        const bodyRows = tableEl.tBodies[0].rows;
        for (let i = 0; i < bodyRows.length; i++) {
            const row = bodyRows[parseInt(i.toString(), 10)];
            const colIndex = parseInt(selectedCols[0].dataset.col || '0', 10);
            const domCol = toDomCol(colIndex, props.enableRowNumbers);
            const cell = row.cells[parseInt(domCol.toString(), 10)];
            if (cell && !cell.classList.contains('e-row-number')) {
                focusedCells.push(cell);
            }
        }
    }
    else {
        // Individual cell focus
        targetEle = tableEl;
        selector = `td.${constants.TABLE_CELL_FOCUS}, th.${constants.TABLE_CELL_FOCUS}`;
        focusedCells = Array.from(targetEle.querySelectorAll(selector));
    }
    const dataPositions = focusedCells.map((td) => ({
        r: toModelRow(parseInt(td.dataset.row, 10), props.enableHeader),
        c: parseInt(td.dataset.col, 10)
    }));
    const minR = Math.min(...dataPositions.map((p) => p.r));
    const maxR = Math.max(...dataPositions.map((p) => p.r));
    const minC = Math.min(...dataPositions.map((p) => p.c));
    const maxC = Math.max(...dataPositions.map((p) => p.c));
    const height = maxR - minR + 1;
    const width = maxC - minC + 1;
    const cells = [];
    for (let r = 0; r < height; r++) {
        const rowCells = [];
        for (let c = 0; c < width; c++) {
            rowCells.push(props.rows[minR + r].cells[minC + c].blocks);
        }
        cells.push(rowCells);
    }
    return {
        type: 'table',
        mode: 'cells',
        cells,
        meta: { rows: height, cols: width, enableHeader: !!props.enableHeader, enableRowNumbers: !!props.enableRowNumbers }
    };
}
export function extractPlainTextMatrixFromPayload(payload, blockModel) {
    const matrix = [];
    const p = (payload.mode === 'table' ? payload.table.props : blockModel.properties);
    if (p.enableHeader) {
        const headerRow = [];
        for (let i = 0; i < payload.meta.cols; i++) {
            const column = p.columns[i];
            const headerText = column.headerText;
            headerRow.push(headerText);
        }
        matrix.push(headerRow);
    }
    if (payload.mode === 'table' && payload.table && payload.table.props) {
        const colCount = (p.columns).length;
        p.rows.forEach((row) => {
            const cells = new Array(colCount).fill('');
            (row.cells).forEach((cell, idx) => {
                const blocks = cell.blocks;
                const cellText = blocks.map((b) => getBlockText(b)).join(' ');
                cells[idx] = cellText;
            });
            matrix.push(cells);
        });
        return matrix;
    }
    // For non-table modes, approximate to rectangular text for fallback
    if (payload.cells && payload.cells.length) {
        payload.cells.forEach((rowCells) => {
            const rowTexts = rowCells.map((cellBlocks) => cellBlocks.map((b) => getBlockText(b)).join(' '));
            matrix.push(rowTexts);
        });
    }
    return matrix;
}
export function createCellsPayloadFromExternal(html, text) {
    let matrix = [];
    if (html && /<table[\s\S]*?>[\s\S]*?<\/table>/i.test(html)) {
        const div = document.createElement('div');
        div.innerHTML = html;
        // For cell paste, always ignore headers
        const tbody = div.querySelector('table tbody');
        if (tbody) {
            const rows = Array.from(tbody.querySelectorAll('tr'));
            const tmp = [];
            for (const trow of rows) {
                const cells = Array.from(trow.querySelectorAll('th,td'));
                const filtered = cells.filter((c) => !c.classList.contains('e-row-number'));
                if (filtered.length) {
                    tmp.push(filtered.map((c) => (c.textContent).trim()));
                }
            }
            const maxCols = Math.max(0, ...tmp.map((r) => r.length));
            tmp.forEach((r) => { while (r.length < maxCols) {
                r.push('');
            } });
            matrix = tmp;
        }
    }
    else if (text && (text.indexOf('\t') !== -1 || /\r?\n.*\t/.test(text))) {
        matrix = text.split(/\r?\n/).filter((l) => l.length > 0).map((l) => l.split('\t'));
    }
    if (!matrix.length) {
        return null;
    }
    const cells = matrix.map((row) => row.map((cell) => [BlockFactory.createParagraphBlock({ content: [BlockFactory.createTextContent({ content: cell })] })]));
    return {
        type: 'table',
        mode: 'cells',
        meta: { rows: matrix.length, cols: matrix[0].length, enableHeader: false, enableRowNumbers: false },
        cells
    };
}
export function tsvFromMatrix(matrix) {
    return matrix.map((row) => row.map((c) => c.replace(/\t/g, ' ').replace(/\r?\n/g, ' ')).join('\t')).join('\n');
}
export function matrixFromTsv(text) {
    return text.split(/\r?\n/).filter((l) => l.length > 0).map((l) => l.split('\t'));
}
export function htmlTableFromMatrix(matrix, options = {}) {
    const hasHeader = !!options.hasHeader;
    // const rn: boolean = !!options.hasRowNumbers;
    const rows = [];
    const bodyStart = hasHeader ? 1 : 0;
    if (hasHeader) {
        const hdrCells = matrix[0].map((text) => `<th>${escapeHtml(text)}</th>`);
        rows.push(`<thead><tr>${hdrCells.join('')}</tr></thead>`);
    }
    const bodyRows = [];
    for (let r = bodyStart; r < matrix.length; r++) {
        const tds = matrix[r].map((text) => `<td>${escapeHtml(text)}</td>`);
        bodyRows.push(`<tr>${tds.join('')}</tr>`);
    }
    rows.push(`<tbody>${bodyRows.join('')}</tbody>`);
    return `<table>${rows.join('')}</table>`;
}
function escapeHtml(s) {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
